import type { ChangeEvent } from 'react';
import { useMemo } from 'react';
import { handleImageUpload, removeImage, createImagePreviews } from '@skin-seoul/service/src/utils/review/imageUtils';
import { Section } from './common/SharedStyles';
import { SPACING } from './common/constants';
import { renderProductIntro, renderRatingSection, renderSkinInfoSection, renderReviewContentSection, renderTitleSection, renderImageUploadSection, renderRecommendationSection, renderNicknameSection, renderEmailSection, renderTermsSection, renderSubmitSection, ReviewRewardBanner } from './common/formHelpers';
import type { ReviewFormProps } from '@skin-seoul/service';
import { Box, VStack } from '@skin-seoul/design-system';
const MobileSection = ({
  children,
  style
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) => <Section style={{
  marginBottom: SPACING['2xl'],
  ...style
}} data-sentry-element="Section" data-sentry-component="MobileSection" data-sentry-source-file="ReviewWriteForm.tsx">{children}</Section>;
interface ReviewWriteFormProps extends ReviewFormProps {
  variant: 'mobile' | 'pc';
}
export const ReviewWriteForm = ({
  formData,
  formActions,
  options,
  productInfo,
  isSubmitDisabled,
  onSubmit,
  variant
}: ReviewWriteFormProps) => {
  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleImageUpload(e, formData.images, formActions.setImages);
  };
  const handleRemoveImage = (idx: number) => {
    removeImage(idx, formData.images, formActions.setImages);
  };
  const imagePreviews = useMemo(() => createImagePreviews(formData.images), [formData.images]);

  // Use mobile-specific components for mobile variant
  const SectionComponent = variant === 'mobile' ? MobileSection : undefined;
  return <>
      <Box w="full" py={4} data-sentry-element="Box" data-sentry-source-file="ReviewWriteForm.tsx">
        <ReviewRewardBanner data-sentry-element="ReviewRewardBanner" data-sentry-source-file="ReviewWriteForm.tsx" />
      </Box>
      {renderProductIntro({
      productInfo
    })}
      <Box width="100%" height="1px" bg="neutral.100" flexShrink="0" margin={{
      base: '16px 0 22px',
      tablet: '32px 0'
    }} data-sentry-element="Box" data-sentry-source-file="ReviewWriteForm.tsx" />
      <VStack gap={{
      base: 10,
      tablet: 6
    }} px={{
      base: '2px',
      tablet: '0'
    }} align="start" w="full" data-sentry-element="VStack" data-sentry-source-file="ReviewWriteForm.tsx">
        {renderRatingSection({
        rating: formData.rating,
        setRating: formActions.setRating,
        SectionComponent
      })}
        {renderTitleSection({
        title: formData.title,
        setTitle: formActions.setTitle
      })}
        {renderReviewContentSection({
        content: formData.content,
        setContent: formActions.setContent
      })}

        {renderImageUploadSection({
        images: formData.images,
        imagePreviews,
        handleImageChange,
        handleRemoveImage
      })}
        {renderRecommendationSection({
        recommend: formData.recommend,
        setRecommend: formActions.setRecommend
      })}
        {renderSkinInfoSection({
        skinType: formData.skinType,
        setSkinType: formActions.setSkinType,
        skinConcern: formData.skinConcern,
        setSkinConcern: formActions.setSkinConcern,
        skinTypeOptions: options.skinTypeOptions,
        skinConcernOptions: options.skinConcernOptions
      })}
        {renderNicknameSection({
        nickname: formData.nickname,
        setNickname: formActions.setNickname
      })}
        {renderEmailSection({
        email: formData.email,
        setEmail: formActions.setEmail
      })}
        {renderTermsSection()}
        {renderSubmitSection({
        isSubmitDisabled,
        onSubmit
      })}
      </VStack>
    </>;
};